import { isFirebaseConfigured } from "@/auth/firebase";
import {
  callGetLeaderboard,
  callGetMyWeeklyBadges,
  callGetRankBadgeConfig,
} from "@/cloud/game-callables";
import {
  isoWeekKeyUtc,
  LEADERBOARD_BOARDS,
  mergeWeeklyBadgeBuffs,
  RANK_BADGE_TOP_RANK,
  rankBadgeBuffToStatBonus,
  type LeaderboardBoard,
  type RankBadgeConfigBoard,
  type UserWeeklyBadges,
} from "@/rank/leaderboard-types";
import type { StatAllocation } from "@/game/model/character-stats";

export type RankCosmeticsState = {
  badgeBoards: LeaderboardBoard[];
  badgeConfigs: RankBadgeConfigBoard[];
  weekly: UserWeeklyBadges | null;
  statBonus: Partial<StatAllocation>;
};

let cachedConfigs: RankBadgeConfigBoard[] | null = null;
let cachedWeekly: UserWeeklyBadges | null = null;

export async function hydrateRankBadgeConfigs(): Promise<RankBadgeConfigBoard[]> {
  if (cachedConfigs) return cachedConfigs;
  if (!isFirebaseConfigured()) {
    cachedConfigs = [];
    return cachedConfigs;
  }
  try {
    const res = await callGetRankBadgeConfig();
    cachedConfigs = res.boards ?? [];
  } catch {
    cachedConfigs = [];
  }
  return cachedConfigs;
}

function uniqueBoards(boards: LeaderboardBoard[]): LeaderboardBoard[] {
  return LEADERBOARD_BOARDS.filter((b) => boards.includes(b));
}

async function loadLiveBoardRanks(): Promise<{ board: LeaderboardBoard; rank: number }[]> {
  if (!isFirebaseConfigured()) return [];
  const rows = await Promise.all(
    LEADERBOARD_BOARDS.map(async (board) => {
      try {
        const res = await callGetLeaderboard(board);
        const rank = res.me?.rank;
        if (typeof rank !== "number" || !Number.isFinite(rank) || rank < 1) return null;
        return { board, rank };
      } catch {
        return null;
      }
    }),
  );
  return rows.filter((row): row is { board: LeaderboardBoard; rank: number } => row != null);
}

async function loadWeeklyBadges(): Promise<UserWeeklyBadges | null> {
  if (!isFirebaseConfigured()) return cachedWeekly;
  try {
    const res = await callGetMyWeeklyBadges();
    cachedWeekly = res.weekly;
    return cachedWeekly;
  } catch {
    return cachedWeekly;
  }
}

export async function loadMyRankCosmetics(): Promise<RankCosmeticsState> {
  const [badgeConfigs, liveRanks, weekly] = await Promise.all([
    hydrateRankBadgeConfigs(),
    loadLiveBoardRanks(),
    loadWeeklyBadges(),
  ]);
  const currentWeek = isoWeekKeyUtc();
  const grants =
    weekly && weekly.weekKey === currentWeek && Date.now() < weekly.expiresAt
      ? weekly.grants
      : [];
  const liveTopBoards = liveRanks
    .filter((row) => row.rank <= RANK_BADGE_TOP_RANK)
    .map((row) => row.board);
  const badgeBoards = uniqueBoards([...grants.map((g) => g.board), ...liveTopBoards]);
  const buff = mergeWeeklyBadgeBuffs(grants);
  const statBonus = rankBadgeBuffToStatBonus(buff) as Partial<StatAllocation>;
  return { badgeBoards, badgeConfigs, weekly, statBonus };
}
